<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <div
    v-if="enabled"
    class="cart-drawer critical-component-hide"
  >
    <div class="cart-drawer__mobile-title">
      <p
        class="text-heading-m"
        v-text="$string('cart.mini_title')"
      />

      <div
        class="overlay__close-container"
        :class="[
          $blockClass(blockClass, 'close-container'),
          $blockClass(responsiveType, 'close-container'),
        ]"
      >
        <button
          class="overlay__close icon-button"
          :class="[
            $blockClass(blockClass, 'close'),
            $blockClass(responsiveType, 'close'),
          ]"
          type="button"
          @click.prevent="close('button')"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.close')"
          />

          <slot
            name="close"
            aria-hidden="true"
          >
            <icon-close-alt
              v-if="useCloseAlt"
            />
            <icon-close
              v-else
            />
          </slot>
        </button>
      </div>
    </div>
    <form
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="cart-drawer__form"
      :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
      method="post"
    >
      <div
        class="cart-drawer__line-items"
        js-overlay="scrollElement"
      >
        <template
          v-for="(lineItem) of cartItems"
          :key="getCartItemKey(lineItem)"
        >
          <line-item
            v-if="renderLineItem(lineItem)"
            class="cart-drawer__line-item"
            :line-item="lineItem"
            :cart-items="cartItems"
            :show-btn-discount="showBtnDiscount"
            :active-selector="activeSelector"
            :altermessage="lineItem?.alter_message"
            :altercolor="lineItem.alter_color"
            small
            @add-line-item="addNewLineItem"
            @toggle-selector="handleToggleOptionDropdown"
          />
        </template>

        <free-gift
          v-if="freeGift.enabled"
          :cart="cart"
          :gift="freeGift"
        />
        <div
          ref="ssContainer"
          class="cart-drawer__ss-widget"
        />
      </div>

      <cart-summary
        v-if="cart.item_count > 0"
        class="cart-drawer__footer"
        small
        :discounts="parsedDiscounts"
        :showAffirmMessaging="false"
        :showLoyaltyMessaging="false"
        :promotionmessage="promotionMessage"
        @toggle-discount-input="updateFocusTrap"
      />
    </form>

    <p
      v-else
      class="
        cart-drawer__empty
        text-body-m-regular
      "
      v-text="$string('cart.empty.drawer_text')"
    />
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 * @param {Object} freeGift - Free gift settings.
 *
 */
import { mapActions, mapState } from 'vuex'

import CartSummary from '~async/cart-summary/cart-summary'
import FreeGift from '~async/free-gift/free-gift'
import LineItem from '~async/line-item/line-item'
import IconCloseAlt from '~icons/directional-navigation/close-alt.svg'
import IconClose from '~icons/directional-navigation/close.svg'

export default {
  name: 'CartDrawer',

  components: {
    CartSummary,
    LineItem,
    FreeGift,
    IconClose,
    IconCloseAlt,
  },

  props: {
    discounts: {
      type: String,
      default: '[]',
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    freeGift: {
      type: Object,
      default: () => ({}),
    },
    mobileDrawer: {
      type: Boolean,
      default: false,
    },
    promotionMessage: {
      type: Object,
      default: () => ({}),
    },
    altermessage: {
      type: String,
      default: '[]',
    },
    showBtnDiscount: {
      type: String,
      default: 'show',
    },
  },

  data() {
    return {
      activeSelector: false,
      enabled: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },

    parsedDiscounts() {
      try {
        return JSON.parse(this.discounts)
      } catch (error) {
        return []
      }
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      this.updateFocusTrap()
    },

    /**
     * Watch active selector (line item option dropdowns) to update focus trap.
     */
    activeSelector() {
      this.updateFocusTrap()
    },
  },

  beforeMount() {

    /**
     * Prevent component from mounting twice
     */
    if ((this.mobileDrawer && this.isMobile) || (!this.mobileDrawer && !this.isMobile)) {
      this.enabled = true
    }
  },

  mounted() {
    if (!this.enabled) {
      return
    }

    this.$nextTick().then(() => {
      this.loadSearchSpring()
    })

    if (!this.overlay.isActive) {
      return
    }

    this.overlay.update()
    this.setEventListeners()
    this.removeDisabledFreeGift()

  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      updateItem: 'cart/updateItem',
      removeItem: 'cart/updateItem',
    }),

    /**
     * Handle the alter message for cart.
     */
    parsedAlterMessage(item) {
      try {
        const fixedJsonString = this.altermessage.replace(/}\s*{/g, '},{')
        const objArray = JSON.parse(fixedJsonString)
        const filteredMessages = objArray.filter((obj) => obj.linekey && obj.linekey !== '')
        const message = filteredMessages.find((obj) => obj.linekey === item.key)
        return message ? [message] : []
      } catch (error) {
        return []
      }
    },

    /**
     * Handle toggle option dropdown.
     * @param {Object|Boolean} selector - Selector object or false.
     */
    handleToggleOptionDropdown(selector) {
      if (selector === false) {
        this.activeSelector = false
        return
      }

      this.activeSelector = false
      setTimeout(() => {
        this.activeSelector = selector
      }, 300)
    },

    /**
     * Set event listeners.
     * - handles closing dropdowns when clicking outside of them.
     * - handles setting focus trap the first time tab key is pressed.
     */
    setEventListeners() {
      window.addEventListener('click', (event) => {
        event.stopPropagation()
        if (!event.target.classList.contains('js-line-item-option')) {
          this.activeSelector = false
        }
      })

      window.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') {
          this.updateFocusTrap()
        }
      }, { once: true })
    },

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },

    /**
     * Creates the script tag for Search Spring recommendations
     */
    loadSearchSpring() {
      // Check if the script has already been added
      if (this.$refs.ssContainer.querySelector('script')) {
        return
      }

      const script = document.createElement('script')
      script.type = 'searchspring/personalized-recommendations'
      script.setAttribute('profile', 'slide-out-cart')
      this.$refs.ssContainer.appendChild(script)
    },

    /**
     * Add new line item and remove old one.
     * @param {Object} lineItem - Line item to add.
     * @param {String} removalKey - Key of line item to remove.
     */
    async addNewLineItem(lineItem, removalKey) {
      try {
        await this.addItem(lineItem)
        this.removeItem(removalKey)

      } catch (error) {
        this.state = 'error'
      }
    },

    /**
     * Whether to render line item. False for free gift.
     * @param {Object|Boolean} lineItem
     * @returns {Boolean}
     */
    renderLineItem(lineItem) {
      return lineItem && !lineItem.properties?.free_gift
    },

    /**
     * If free gift is disabled in settings while
     * the gift product is in a user's cart, remove it.
     */
    removeDisabledFreeGift() {
      if (this.freeGift.enabled) {
        return
      }

      const giftKey = this.cartItems.find(item => item.properties?.free_gift)?.key

      if (giftKey) {
        this.removeItem(giftKey)
      }
    },

    /**
     * Update drawer focus trap.
     */
    updateFocusTrap() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.update({ currentElement: document.activeElement })
      this.setEventListeners()
    },

    close(source = 'button') {
      this.overlay.close(source)
    },
  },

}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
